import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  LoggerService,
  PalantirButtonComponent,
  PalantirDialogComponent,
  PalantirInputComponent,
  PalantirTextComponent,
  PalantirToastService,
} from '@flux/design-system';

@Component({
  selector: 'flux-debug-logs-configurator',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PalantirDialogComponent,
    PalantirButtonComponent,
    PalantirInputComponent,
    PalantirTextComponent,
  ],
  templateUrl: './debug-logs-configurator.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DebugLogsConfiguratorComponent {
  private readonly STORAGE_KEY = 'FLUX_CONTROL.DEBUG';

  public readonly currentPatterns = signal<string>(localStorage.getItem(this.STORAGE_KEY) ?? '');

  public readonly newPatternInput = signal<string>('');

  public readonly patternList = computed(() => {
    const p = this.currentPatterns();
    return p ? p.split(',').filter(Boolean) : [];
  });

  public readonly presets = [
    { label: 'Tudo', value: '*' },
    { label: 'Preferencias do Jão', value: '*#error,*#warn,flux:socket' },
    { label: 'Apenas Erros', value: '*#error' },
    { label: 'Apenas Info', value: '*#info' },
    { label: 'Tudo menos debug', value: '*,-*#debug' },
    { label: 'Flux Core', value: 'flux:*' },
    { label: 'Design-System', value: 'ds:*' },
    { label: 'Realtime', value: 'flux:socket:*' },
  ];

  public constructor(private readonly toastService: PalantirToastService) {}

  public addPattern(): void {
    const pattern = this.newPatternInput().trim();
    if (!pattern) {
      return;
    }

    const current = this.currentPatterns();
    const patterns = current ? current.split(',').filter(Boolean) : [];

    if (patterns.includes(pattern)) {
      this.toastService.showWarning(`Padrão "${pattern}" já está ativo.`);
      return;
    }

    patterns.push(pattern);
    this.updateConfig(patterns.join(','));
    this.newPatternInput.set('');
  }

  public removePattern(pattern: string): void {
    const patterns = this.currentPatterns()
      .split(',')
      .filter((p) => p !== pattern);
    this.updateConfig(patterns.join(','));
  }

  public enableAll(): void {
    this.updateConfig('*');
  }

  public disableAll(): void {
    this.updateConfig('');
  }

  public applyPreset(preset: string): void {
    const current = this.currentPatterns();
    const patterns = current ? current.split(',').filter(Boolean) : [];

    if (patterns.includes(preset)) {
      return;
    }

    patterns.push(preset);
    this.updateConfig(patterns.join(','));
  }

  private updateConfig(patterns: string): void {
    this.currentPatterns.set(patterns);
    if (patterns) {
      LoggerService.enable(patterns);
    } else {
      LoggerService.disable();
    }
  }
}
